import { useEffect, useRef } from 'react';

export function FeedNextPage({
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError = false,
  fetchNextPage,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError?: boolean;
  fetchNextPage: () => unknown;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchNextPage();
        }
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage]);

  if (!hasNextPage && !isFetchingNextPage && !isFetchNextPageError) return null;

  return (
    <div ref={sentinelRef} className="mt-3 min-h-px" data-testid="feed-load-more">
      {isFetchNextPageError ? (
        <div
          className="rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground"
          role="alert"
        >
          <p>Couldn’t load more transactions.</p>
          <button
            type="button"
            className="mt-2 min-h-11 rounded-md border border-input px-3"
            onClick={() => void fetchNextPage()}
          >
            Try again
          </button>
        </div>
      ) : isFetchingNextPage ? (
        <p role="status" className="text-sm text-muted-foreground">Loading…</p>
      ) : null}
    </div>
  );
}

export default FeedNextPage;
